import { useState } from 'react';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loadList, upsertList } from '../../lib/localList';
import type { Assignment, AttendanceLog, CarePlan, Carer, CarerPayrunResult } from '../../lib/types';
import { EmptyState, ErrorNote, Field, PageHeader, StatusBadge } from '../../ui/components';

const ASSESSOR_ROLES = ['ASSESSOR', 'HOMECARE_COORDINATOR', 'ASP_ADMIN'];
const COORDINATOR_ROLES = ['HOMECARE_COORDINATOR', 'ASP_ADMIN'];

/**
 * S04 has NO GET routes at all — every screen a home-care system needs
 * (plans, carers, assignments, attendance) has to be assembled client-side
 * from the real responses of the POST actions that create/advance them. See
 * this app's build report.
 */
export function HomeCare() {
  const { persona } = useAuth();
  const canAssess = persona?.roles.some((r) => ASSESSOR_ROLES.includes(r)) ?? false;
  const canCoordinate = persona?.roles.some((r) => COORDINATOR_ROLES.includes(r)) ?? false;

  const [plans, setPlans] = useState<CarePlan[]>(() => loadList<CarePlan>('homecare.plans'));
  const [carers, setCarers] = useState<Carer[]>(() => loadList<Carer>('homecare.carers'));
  const [assignments, setAssignments] = useState<Assignment[]>(() => loadList<Assignment>('homecare.assignments'));
  const [attendance, setAttendance] = useState<AttendanceLog[]>(() => loadList<AttendanceLog>('homecare.attendance'));
  const [payrun, setPayrun] = useState<CarerPayrunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // form state
  const [beneficiaryNin, setBeneficiaryNin] = useState('');
  const [dependencyScore, setDependencyScore] = useState('60');
  const [hoursApproved, setHoursApproved] = useState('40');
  const [carerNin, setCarerNin] = useState('');
  const [carerName, setCarerName] = useState('');
  const [assignPlanId, setAssignPlanId] = useState('');
  const [assignCarerId, setAssignCarerId] = useState('');
  const [assignDay, setAssignDay] = useState('Monday');
  const [assignHours, setAssignHours] = useState('4');
  const [attendAssignmentId, setAttendAssignmentId] = useState('');
  const [attendDate, setAttendDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [attendHours, setAttendHours] = useState('4');
  const [confirmAttendanceId, setConfirmAttendanceId] = useState('');
  const [confirmedBy, setConfirmedBy] = useState('');
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [valueDate, setValueDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [debtorAccountId, setDebtorAccountId] = useState('');

  if (!canAssess && !canCoordinate) {
    return (
      <div>
        <PageHeader title="Home Care" />
        <ErrorNote message="This view requires the Care Needs Assessor, Home Care Coordinator, or ASP Administrator role." />
      </div>
    );
  }

  const run = async <T,>(fn: () => Promise<T>): Promise<T | null> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
      return null;
    } finally {
      setBusy(false);
    }
  };

  const assess = () =>
    run(async () => {
      const plan = await api.post<CarePlan>('/api/v1/homecare/assessments', {
        beneficiaryNin,
        dependencyScore: Number.parseInt(dependencyScore, 10),
        hoursApproved: Number.parseInt(hoursApproved, 10),
      });
      setPlans(upsertList('homecare.plans', plan));
      setBeneficiaryNin('');
    });

  const approvePlan = (planId: string) =>
    run(async () => {
      const plan = await api.post<CarePlan>(`/api/v1/homecare/plans/${planId}/approve`);
      setPlans(upsertList('homecare.plans', plan));
    });

  const registerCarer = () =>
    run(async () => {
      const carer = await api.post<Carer>('/api/v1/homecare/carers', { nin: carerNin, fullName: carerName, trainingCerts: [] });
      setCarers(upsertList('homecare.carers', carer));
      setCarerNin('');
      setCarerName('');
    });

  const assign = () =>
    run(async () => {
      const assignment = await api.post<Assignment>('/api/v1/homecare/assignments', {
        carePlanId: assignPlanId,
        carerId: assignCarerId,
        schedule: [{ day: assignDay, hours: Number.parseFloat(assignHours) }],
      });
      setAssignments(upsertList('homecare.assignments', assignment));
    });

  const logAttendance = () =>
    run(async () => {
      const log = await api.post<AttendanceLog>(`/api/v1/homecare/assignments/${attendAssignmentId}/attendance`, {
        date: attendDate,
        hours: Number.parseFloat(attendHours),
      });
      setAttendance(upsertList('homecare.attendance', log));
    });

  const confirmAttendance = () =>
    run(async () => {
      const log = await api.post<AttendanceLog>(`/api/v1/homecare/attendance/${confirmAttendanceId}/confirm`, { confirmedBy });
      setAttendance(upsertList('homecare.attendance', log));
      setConfirmAttendanceId('');
      setConfirmedBy('');
    });

  const computePayrun = () =>
    run(async () => {
      const result = await api.post<CarerPayrunResult>(`/api/v1/homecare/payruns/${period}`, {});
      setPayrun(result);
    });

  const sendPayrun = () =>
    run(async () => {
      await api.post(`/api/v1/homecare/payruns/${period}/send`, { valueDate, debtorAccountId });
      setError(`Carer pay run for ${period} sent for value date ${valueDate}.`);
    });

  return (
    <div>
      <PageHeader title="Home Care Programme" subtitle="Dependency assessment → carer assignment → attendance → monthly carer pay" />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        {canAssess ? (
          <div className="card p-4">
            <h2 className="mb-2 text-sm font-bold text-slate-700">1. Assess dependency → care plan</h2>
            <div className="grid grid-cols-3 gap-2">
              <input className="field-input col-span-3" placeholder="Beneficiary NIN" value={beneficiaryNin} onChange={(e) => setBeneficiaryNin(e.target.value)} />
              <Field label="Dependency score (0-100)">
                <input className="field-input" type="number" min={0} max={100} value={dependencyScore} onChange={(e) => setDependencyScore(e.target.value)} />
              </Field>
              <Field label="Hours approved / month">
                <input className="field-input" type="number" min={1} value={hoursApproved} onChange={(e) => setHoursApproved(e.target.value)} />
              </Field>
            </div>
            <button className="btn-primary mt-2" onClick={assess} disabled={busy || !beneficiaryNin}>
              Submit assessment
            </button>

            <div className="mt-3">
              {plans.length === 0 ? (
                <EmptyState title="No care plans yet" />
              ) : (
                <ul className="space-y-1 text-xs">
                  {plans.map((p) => (
                    <li key={p.id} className="flex items-center justify-between rounded border border-slate-100 px-2 py-1">
                      <span>
                        {p.beneficiaryNin} · score {p.dependencyScore} · {p.hoursApproved}h/mo <StatusBadge status={p.status} />
                      </span>
                      {canCoordinate && p.status === 'ASSESSED' ? (
                        <button className="btn-secondary !px-2 !py-0.5 text-xs" onClick={() => approvePlan(p.id)} disabled={busy}>
                          Approve
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}

        {canCoordinate ? (
          <div className="card p-4">
            <h2 className="mb-2 text-sm font-bold text-slate-700">2. Register carer</h2>
            <div className="grid grid-cols-2 gap-2">
              <input className="field-input" placeholder="Carer NIN" value={carerNin} onChange={(e) => setCarerNin(e.target.value)} />
              <input className="field-input" placeholder="Full name" value={carerName} onChange={(e) => setCarerName(e.target.value)} />
            </div>
            <button className="btn-primary mt-2" onClick={registerCarer} disabled={busy || !carerNin || !carerName}>
              Register
            </button>
            <div className="mt-3">
              {carers.length === 0 ? (
                <EmptyState title="No carers registered yet" />
              ) : (
                <ul className="space-y-1 text-xs">
                  {carers.map((c) => (
                    <li key={c.id} className="rounded border border-slate-100 px-2 py-1">
                      {c.fullName} ({c.nin}) — id <span className="text-slate-400">{c.id}</span> <StatusBadge status={c.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}

        {canCoordinate ? (
          <div className="card p-4">
            <h2 className="mb-2 text-sm font-bold text-slate-700">3. Assign carer to plan</h2>
            <div className="grid grid-cols-2 gap-2">
              <input className="field-input" placeholder="Care plan id" value={assignPlanId} onChange={(e) => setAssignPlanId(e.target.value)} />
              <input className="field-input" placeholder="Carer id" value={assignCarerId} onChange={(e) => setAssignCarerId(e.target.value)} />
              <input className="field-input" placeholder="Day (e.g. Monday)" value={assignDay} onChange={(e) => setAssignDay(e.target.value)} />
              <input className="field-input" type="number" placeholder="Hours" value={assignHours} onChange={(e) => setAssignHours(e.target.value)} />
            </div>
            <button className="btn-primary mt-2" onClick={assign} disabled={busy || !assignPlanId || !assignCarerId}>
              Assign
            </button>
            <div className="mt-3">
              {assignments.length === 0 ? (
                <EmptyState title="No assignments yet" />
              ) : (
                <ul className="space-y-1 text-xs">
                  {assignments.map((a) => (
                    <li key={a.id} className="rounded border border-slate-100 px-2 py-1">
                      plan {a.carePlanId} × carer {a.carerId} — id <span className="text-slate-400">{a.id}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}

        <div className="card p-4">
          <h2 className="mb-2 text-sm font-bold text-slate-700">4. Attendance</h2>
          <div className="grid grid-cols-3 gap-2">
            <input className="field-input col-span-3" placeholder="Assignment id" value={attendAssignmentId} onChange={(e) => setAttendAssignmentId(e.target.value)} />
            <input className="field-input" type="date" value={attendDate} onChange={(e) => setAttendDate(e.target.value)} />
            <input className="field-input" type="number" placeholder="Hours" value={attendHours} onChange={(e) => setAttendHours(e.target.value)} />
            <button className="btn-secondary" onClick={logAttendance} disabled={busy || !attendAssignmentId}>
              Log
            </button>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <input className="field-input col-span-2" placeholder="Attendance log id to confirm" value={confirmAttendanceId} onChange={(e) => setConfirmAttendanceId(e.target.value)} />
            <input className="field-input" placeholder="Confirmed by" value={confirmedBy} onChange={(e) => setConfirmedBy(e.target.value)} />
          </div>
          <button className="btn-secondary mt-2" onClick={confirmAttendance} disabled={busy || !confirmAttendanceId || !confirmedBy}>
            Confirm (family sign-off)
          </button>
          <div className="mt-3">
            {attendance.length === 0 ? (
              <EmptyState title="No attendance logged yet" />
            ) : (
              <ul className="space-y-1 text-xs">
                {attendance.map((a) => (
                  <li key={a.id} className="rounded border border-slate-100 px-2 py-1">
                    {a.date} · {a.hours}h — {a.confirmedBy ? `confirmed by ${a.confirmedBy}` : 'unconfirmed'}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {canCoordinate ? (
          <div className="card p-4 lg:col-span-2">
            <h2 className="mb-2 text-sm font-bold text-slate-700">5. Monthly carer pay run</h2>
            <div className="grid grid-cols-4 gap-2">
              <input className="field-input" placeholder="YYYY-MM" value={period} onChange={(e) => setPeriod(e.target.value)} />
              <button className="btn-secondary" onClick={computePayrun} disabled={busy}>
                Compute
              </button>
              <input className="field-input" type="date" value={valueDate} onChange={(e) => setValueDate(e.target.value)} />
              <input className="field-input" placeholder="Debtor account id" value={debtorAccountId} onChange={(e) => setDebtorAccountId(e.target.value)} />
            </div>
            <button className="btn-primary mt-2" onClick={sendPayrun} disabled={busy || !debtorAccountId}>
              Send to Treasury (F06)
            </button>
            {payrun ? (
              <div className="mt-3 text-xs">
                {payrun.carers} carer(s), rate {scr(payrun.hourlyRate)}/hour
                <ul className="mt-1 space-y-0.5">
                  {payrun.items.map((it) => (
                    <li key={it.id}>
                      carer {it.carerId} — {it.hours}h → {scr(it.computed)} <StatusBadge status={it.status} />
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
